import React, { useEffect, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { MapContainer } from './styles';
import Location from './location';

const { VITE_MAPBOX_GLJS_API, VITE_MAPBOX_GLJS_STYLE } = import.meta.env;

const FIT_BOUNDS_OPTIONS = { padding: 50, maxZoom: 15, linear: true };

function applyBounds(map) {
  const bounds = map?.app?.locationsBound;
  if (!map || !bounds) {
    return;
  }
  map.resize();
  map.fitBounds(bounds, FIT_BOUNDS_OPTIONS);
}

function updateMap(map, data) {
  const { locationsBound, ...geojson } = Location.getGeoJSON(data);
  const location = map.getSource('location');
  if (location) {
    location.setData(geojson.data);
    map.app.data = data;
    map.app.locationsBound = locationsBound;
    applyBounds(map);
  }
}

function initMap(data, container, theme, helper, options) {
  const { mapboxgl } = window;
  const { locationsBound, ...geojson } = Location.getGeoJSON(data);

  mapboxgl.accessToken = VITE_MAPBOX_GLJS_API;

  const map = new mapboxgl.Map({
    container,
    style: VITE_MAPBOX_GLJS_STYLE,
    bounds: locationsBound,
    fitBoundsOptions: { padding: 50 },
    maxZoom: 17,
    minZoom: 8,
    showZoom: true
  });
  map.app = {
    theme,
    locationsBound,
    data,
    helper,
    options
  };

  const nav = new mapboxgl.NavigationControl({ showCompass: false });
  map.addControl(nav, 'top-right');

  // disable map rotation using right click + drag
  map.dragRotate.disable();

  // disable map rotation using touch rotation gesture
  map.touchZoomRotate.disableRotation();

  map.on('load', async () => {
    map.addSource('location', geojson);
    Location.addLayer(map);
    Location.addPlaceHover();
    Location.addPlaceClick();

    /* const drawClusters = Locations.memoDrawClusters(map);
    map.on('render', () => {
      if (!map.isSourceLoaded('locations')) return;
      drawClusters();
    }); */

    // add routes

    // helper.setMap(map);
    applyBounds(map);
  });

  return map;
}

const Map = props => {
  const {
    mapClass = '',
    id,
    height = 300,
    width = '100%',
    disabled = false,
    measurementSystem,
    radius,
    change,
    member
  } = props;

  const input = props.input || {
    value: props.value,
    onChange: props.onChange
  };

  const rawValue = input?.value ?? props.value;
  const location = Array.isArray(rawValue) ? rawValue[0] : rawValue;
  const hasValidLocation = Boolean(location && (typeof location.lat === 'number' || typeof location.lng === 'number'));
  const onChange = input?.onChange || props.onChange;
  const theme = useTheme();
  const style = useMemo(() => ({ height, width }), [height, width]);
  const container = `map_id_${id}`;
  const containerRef = useRef(null);
  const map = useRef(null);

  useEffect(() => {
    if (!map.current && hasValidLocation && containerRef.current) {
      if (!window.mapboxgl) return undefined;
      map.current = initMap(
        { location, radius },
        containerRef.current,
        theme,
        { changeLocation: onChange, changeField: change },
        { member, measurementSystem, disabled }
      );
      setTimeout(() => {
        map.current?.resize();
      }, 0);
    } else if (map.current && hasValidLocation) {
      updateMap(map.current, { location, radius });
    }
    return () => {
      window.clearInterval(map.current?.app?.timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValidLocation, location, radius]);

  useEffect(() => () => {
    if (map.current) {
      try {
        map.current.remove();
      } catch (e) {
        // ignore cleanup errors
      }
      map.current = null;
    }
  }, []);

  if (!hasValidLocation) {
    return null;
  }

  return (
    <MapContainer className={mapClass}>
      <div id={container} ref={containerRef} style={style} />
    </MapContainer>
  );
};

export default Map;
